import { useEffect, useRef } from "react";
import { products } from "@/mocks/products";
import { Link } from "react-router-dom";

export default function FeaturedProducts() {
  const cardsRef = useRef<HTMLDivElement>(null);
  const featuredProducts = products.filter((p) => p.isFeatured);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("opacity-100", "translate-y-0");
            entry.target.classList.remove("opacity-0", "translate-y-8");
          }
        });
      },
      { threshold: 0.1 }
    );

    const cards = cardsRef.current?.children;
    if (cards) {
      Array.from(cards).forEach((card, index) => {
        card.classList.add("opacity-0", "translate-y-8", "transition-all", "duration-700");
        (card as HTMLElement).style.transitionDelay = `${index * 150}ms`;
        observer.observe(card);
      });
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section id="featured-products" className="py-20 md:py-28 bg-dark-50">
      <div className="w-full px-4 sm:px-6 lg:px-8 xl:px-12">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-4">
            <div>
              <span className="inline-block text-dark-400 text-xs font-semibold uppercase tracking-widest mb-4">
                Proizvodi
              </span>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-dark-900 leading-tight">
                Izdvajamo iz ponude
              </h2>
            </div>
            <Link
              to="/products"
              className="inline-flex items-center gap-2 text-brand-600 hover:text-brand-700 text-sm font-semibold transition-colors whitespace-nowrap"
            >
              Svi proizvodi
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>

          <div ref={cardsRef} className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {featuredProducts.map((product, index) => (
              <Link
                key={index}
                to={`/product/${product.id}`}
                className="group bg-white rounded-2xl border border-dark-100 overflow-hidden transition-all duration-300 hover:-translate-y-2"
              >
                <div className="aspect-square bg-dark-50/50 flex items-center justify-center p-8 overflow-hidden">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-contain object-center group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                </div>
                <div className="p-5">
                  <span className="text-xs font-semibold text-accent-600 uppercase tracking-wider">
                    {product.category}
                  </span>
                  <h3 className="text-lg font-bold text-dark-900 mt-1 mb-2 group-hover:text-brand-600 transition-colors">
                    {product.name}
                  </h3>
                  <p className="text-sm text-dark-400 leading-relaxed mb-4 line-clamp-2">
                    {product.shortDesc}
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="text-lg font-bold text-dark-900">
                      {product.price}
                    </span>
                    <span className="w-10 h-10 flex items-center justify-center rounded-full bg-brand-50 text-brand-600 group-hover:bg-brand-500 group-hover:text-white transition-all">
                      <i className="ri-arrow-right-up-line"></i>
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}